import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { z } from 'zod';
import { blockablePath, blockRuleLink } from '../lib/block-page.js';
import { Card } from '../ui/Card.js';
import { DataTable, type Column } from '../ui/DataTable.js';

/**
 * What a web scanner asked for (P11.18), from a CrowdSec event's `raw`: each
 * method and path the alert's events named, how often and what the site answered.
 * Read defensively: it is the agent's summary of text an attacker wrote, so it is
 * parsed again here, and shown only ever as text.
 */

const Requests = z.array(
  z.object({
    method: z.string().max(10).optional(),
    path: z.string().max(400),
    status: z.number().int().optional(),
    host: z.string().max(253).optional(),
    count: z.number().int().positive(),
  }),
);
type Seen = z.infer<typeof Requests>[number];

const Raw = z.object({ crowdsec: z.object({ requests: Requests }) });

/** The requests an event's `raw` carries, or none. */
const requestsIn = (raw: unknown): Seen[] => {
  const parsed = Raw.safeParse(raw);
  return parsed.success ? parsed.data.crowdsec.requests : [];
};

export function EventRequests({
  raw,
  canMakeRules,
  serverId,
}: {
  raw: unknown;
  canMakeRules: boolean;
  serverId: string;
}): ReactNode {
  const requests = requestsIn(raw);
  if (requests.length === 0) return null;

  const columns: Column<Seen>[] = [
    {
      key: 'path',
      header: 'Asked for',
      rowHeader: true,
      cell: (row) => (
        <>
          <code>{row.path}</code>
          {row.host !== undefined && <span className="cell-sub">on {row.host}</span>}
        </>
      ),
    },
    { key: 'method', header: 'Method', cell: (row) => row.method ?? '—' },
    {
      key: 'status',
      header: 'Answered',
      cell: (row) => (row.status === undefined ? '—' : String(row.status)),
    },
    { key: 'count', header: 'Times', align: 'end', cell: (row) => row.count },
    ...(canMakeRules
      ? [
          {
            key: 'rule',
            header: 'Next',
            cell: (row: Seen) =>
              blockablePath(row.path).ok ? (
                <Link to={blockRuleLink(row.path, { serverId })}>Block this page</Link>
              ) : (
                <span className="muted">—</span>
              ),
          },
        ]
      : []),
  ];

  return (
    <Card
      title="What it asked for"
      description="The pages this address requested, from the log lines that made CrowdSec act. Query strings are cut off."
    >
      <DataTable
        caption="Pages requested by this address"
        columns={columns}
        rows={requests}
        rowKey={(row) => `${row.method ?? ''} ${row.path}`}
      />
    </Card>
  );
}
